// AiUsageView — admin de plataforma vê quanto de tokens IA cada usuário
// consumiu, separando o que veio da chave da plataforma (custo nosso) do
// que veio da BYOK (custo do aluno).
//
// Layout:
//   - KPI strip no topo (range selector + 4 cards: tokens totais, custo USD
//     plataforma, % BYOK, usuários únicos)
//   - Bloco "Por feature" + "Por provider/modelo"
//   - Top 10 usuários (com drill-down ao clicar)
//   - Modal drill-down: histórico de events daquele user com filtros

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  Activity,
  AlertCircle,
  Coins,
  Cpu,
  DollarSign,
  KeyRound,
  Loader2,
  TrendingUp,
  Users,
  X
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import {
  api,
  ApiError,
  type AiUsageSummaryResponse,
  type AiUsageTopUser,
  type AiCostTableRow
} from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { openAlert, openPrompt } from "./ConfirmPromptModal";

const phase = signal<"loading" | "ready" | "error">("loading");
const data = signal<AiUsageSummaryResponse | null>(null);
const errMsg = signal<string | null>(null);
const range = signal<"7d" | "30d" | "90d" | "365d">("30d");
const accessFilter = signal<"all" | "platform" | "byok">("all");

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.aiUsageSummary(range.value, {
      accessMode: accessFilter.value === "all" ? undefined : accessFilter.value
    });
    data.value = r;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function formatUsdCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

// Custo EXATO a partir de nanodólares (1e-9 USD). Mostra todas as casas pra
// não esconder a soma de eventos sub-centavo. [[project-token-tracking-billing]]
function formatUsdNano(nano: number, decimals = 6): string {
  return `$${(Number(nano) / 1e9).toFixed(decimals)}`;
}

// Por linha de evento: mostra "<$0.01" quando é pequeno mas > 0 (deixa claro
// que não é grátis), senão o valor exato.
function formatUsdNanoRow(nano: number | null): string {
  if (nano == null) return "—";
  const usd = Number(nano) / 1e9;
  if (usd === 0) return "$0.00";
  if (usd < 0.01) return "< $0.01";
  return `$${usd.toFixed(2)}`;
}

export function AiUsageView() {
  useEffect(() => {
    void load();
  }, []);

  const [drillUser, setDrillUser] = useState<AiUsageTopUser | null>(null);
  const [showCostTable, setShowCostTable] = useState(false);

  if (phase.value === "loading") {
    return (
      <div class="space-y-3">
        <Skeleton class="h-12 w-full" />
        <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} class="h-24 w-full" />
          ))}
        </div>
        <Skeleton class="h-80 w-full" />
      </div>
    );
  }
  if (phase.value === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {errMsg.value}
      </div>
    );
  }
  const d = data.value;
  if (!d) return null;
  const o = d.overall;
  const tokensTotal = Number(o?.totalTokens ?? 0);
  const tokensPlatform = Number(o?.tokensPlatform ?? 0);
  const tokensByok = Number(o?.tokensByok ?? 0);
  const platformPct = tokensTotal > 0 ? Math.round((tokensPlatform / tokensTotal) * 100) : 0;
  const byokPct = tokensTotal > 0 ? Math.round((tokensByok / tokensTotal) * 100) : 0;

  return (
    <div class="space-y-5">
      {/* Controles */}
      <div class="flex flex-wrap items-center justify-between gap-2">
        <div class="flex flex-wrap gap-1">
          {(["7d", "30d", "90d", "365d"] as const).map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => {
                range.value = r;
                void load();
              }}
              class={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                range.value === r
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-accent"
              }`}
            >
              {r === "7d" && "Últimos 7 dias"}
              {r === "30d" && "Últimos 30 dias"}
              {r === "90d" && "Últimos 90 dias"}
              {r === "365d" && "Último ano"}
            </button>
          ))}
        </div>
        <div class="flex flex-wrap items-center gap-1">
          {(["all", "platform", "byok"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => {
                accessFilter.value = m;
                void load();
              }}
              class={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                accessFilter.value === m
                  ? "bg-violet-500/15 text-violet-700 dark:text-violet-300"
                  : "bg-muted text-muted-foreground hover:bg-accent"
              }`}
            >
              {m === "all" && "Plataforma + BYOK"}
              {m === "platform" && "Só plataforma (paga)"}
              {m === "byok" && "Só BYOK (chave do user)"}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setShowCostTable(true)}
            class="ml-2 inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 py-1 text-xs hover:bg-accent"
          >
            <DollarSign className="size-3" />
            Tabela de preços
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          icon={Activity}
          label="Eventos"
          value={String(o?.events ?? 0)}
          tint="bg-blue-500/10 text-blue-600 dark:text-blue-300"
          hint={
            o?.events && o?.okEvents != null
              ? `${o.okEvents} OK · ${o.events - o.okEvents} falhos · ${formatTokens(tokensTotal)} tokens · ${o.uniqueUsers ?? 0} users`
              : `${formatTokens(tokensTotal)} tokens · ${o?.uniqueUsers ?? 0} usuários únicos`
          }
        />
        <KpiCard
          icon={Coins}
          label="Tokens plataforma"
          value={formatTokens(tokensPlatform)}
          tint="bg-violet-500/10 text-violet-600 dark:text-violet-300"
          hint={`${platformPct}% do total — custo nosso`}
        />
        <KpiCard
          icon={KeyRound}
          label="Tokens BYOK"
          value={formatTokens(tokensByok)}
          tint="bg-emerald-500/10 text-emerald-600 dark:text-emerald-300"
          hint={`${byokPct}% do total — chave do aluno`}
        />
        <KpiCard
          icon={DollarSign}
          label="Custo cobrável"
          value={formatUsdNano(Number(o?.costPlatformBillableNano ?? o?.costPlatformNano ?? 0))}
          tint="bg-amber-500/10 text-amber-600 dark:text-amber-300"
          hint={
            o?.costPlatformFailedNano && Number(o.costPlatformFailedNano) > 0
              ? `+ ${formatUsdNano(Number(o.costPlatformFailedNano))} perdido em falhas (não cobrado)`
              : "USD exato (soma sem truncamento, só sucesso)"
          }
        />
      </div>

      {/* Por feature */}
      <section class="rounded-lg border border-border bg-card p-4">
        <h2 class="mb-3 flex items-center gap-2 text-sm font-semibold">
          <TrendingUp className="size-4 text-primary" />
          Consumo por feature
        </h2>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border text-left text-xs text-muted-foreground">
                <th class="py-2">Feature</th>
                <th class="py-2 text-right">Eventos</th>
                <th class="py-2 text-right">Tokens</th>
                <th class="py-2 text-right">Plataforma</th>
                <th class="py-2 text-right">BYOK</th>
                <th class="py-2 text-right">Custo USD</th>
              </tr>
            </thead>
            <tbody>
              {d.byFeature.length === 0 ? (
                <tr>
                  <td colspan={6} class="py-4 text-center text-muted-foreground">
                    Nenhum consumo no período.
                  </td>
                </tr>
              ) : (
                d.byFeature.map((f) => (
                  <tr key={f.feature} class="border-b border-border/50 hover:bg-accent/30">
                    <td class="py-2 font-medium">{f.feature}</td>
                    <td class="py-2 text-right">{f.events}</td>
                    <td class="py-2 text-right">{formatTokens(Number(f.tokens))}</td>
                    <td class="py-2 text-right text-violet-600 dark:text-violet-300">
                      {formatTokens(Number(f.tokensPlatform))}
                    </td>
                    <td class="py-2 text-right text-emerald-600 dark:text-emerald-300">
                      {formatTokens(Number(f.tokensByok))}
                    </td>
                    <td class="py-2 text-right tabular-nums">
                      {formatUsdNano(Number(f.costPlatformNano ?? 0))}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Por provider/modelo */}
      <section class="rounded-lg border border-border bg-card p-4">
        <h2 class="mb-3 flex items-center gap-2 text-sm font-semibold">
          <Cpu className="size-4 text-primary" />
          Consumo por provider/modelo
        </h2>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border text-left text-xs text-muted-foreground">
                <th class="py-2">Provider</th>
                <th class="py-2">Modelo</th>
                <th class="py-2 text-right">Eventos</th>
                <th class="py-2 text-right">Tokens</th>
                <th class="py-2 text-right">Custo USD</th>
              </tr>
            </thead>
            <tbody>
              {d.byProvider.length === 0 ? (
                <tr>
                  <td colspan={5} class="py-4 text-center text-muted-foreground">
                    Sem dados.
                  </td>
                </tr>
              ) : (
                d.byProvider.map((p) => (
                  <tr
                    key={`${p.provider}::${p.model}`}
                    class="border-b border-border/50 hover:bg-accent/30"
                  >
                    <td class="py-2 font-medium">{p.provider}</td>
                    <td class="py-2 font-mono text-xs">{p.model}</td>
                    <td class="py-2 text-right">{p.events}</td>
                    <td class="py-2 text-right">{formatTokens(Number(p.tokens))}</td>
                    <td class="py-2 text-right tabular-nums">
                      {formatUsdNano(Number(p.costPlatformNano ?? 0))}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Top users */}
      <section class="rounded-lg border border-border bg-card p-4">
        <h2 class="mb-3 flex items-center gap-2 text-sm font-semibold">
          <Users className="size-4 text-primary" />
          Top 10 usuários (clique pra drill-down)
        </h2>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border text-left text-xs text-muted-foreground">
                <th class="py-2">Usuário</th>
                <th class="py-2 text-right">Eventos</th>
                <th class="py-2 text-right">Tokens</th>
                <th class="py-2 text-right">Plataforma</th>
                <th class="py-2 text-right">BYOK</th>
                <th class="py-2 text-right">Custo USD</th>
              </tr>
            </thead>
            <tbody>
              {d.topUsers.length === 0 ? (
                <tr>
                  <td colspan={6} class="py-4 text-center text-muted-foreground">
                    Nenhum consumo.
                  </td>
                </tr>
              ) : (
                d.topUsers.map((u) => (
                  <tr
                    key={u.userId}
                    onClick={() => setDrillUser(u)}
                    class="cursor-pointer border-b border-border/50 hover:bg-accent/40"
                  >
                    <td class="py-2">
                      <div class="font-medium">{u.userName}</div>
                      <div class="text-xs text-muted-foreground">{u.userEmail}</div>
                    </td>
                    <td class="py-2 text-right">{u.events}</td>
                    <td class="py-2 text-right">{formatTokens(Number(u.tokens))}</td>
                    <td class="py-2 text-right text-violet-600 dark:text-violet-300">
                      {formatTokens(Number(u.tokensPlatform))}
                    </td>
                    <td class="py-2 text-right text-emerald-600 dark:text-emerald-300">
                      {formatTokens(Number(u.tokensByok))}
                    </td>
                    <td class="py-2 text-right tabular-nums">
                      {formatUsdNano(Number(u.costPlatformNano ?? 0))}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {drillUser && (
        <UserDrillDown user={drillUser} onClose={() => setDrillUser(null)} />
      )}
      {showCostTable && <CostTableModal onClose={() => setShowCostTable(false)} />}
    </div>
  );
}

function KpiCard({
  icon: Icon,
  label,
  value,
  tint,
  hint
}: {
  icon: typeof Activity;
  label: string;
  value: string;
  tint: string;
  hint: string;
}) {
  return (
    <div class="rounded-lg border border-border bg-card p-3">
      <div class={`mb-2 inline-flex size-9 items-center justify-center rounded-md ${tint}`}>
        <Icon className="size-4" />
      </div>
      <div class="text-2xl font-bold tabular-nums">{value}</div>
      <div class="mt-0.5 text-xs font-medium">{label}</div>
      <div class="mt-1 text-[11px] text-muted-foreground">{hint}</div>
    </div>
  );
}

function UserDrillDown({
  user,
  onClose
}: {
  user: AiUsageTopUser;
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [resp, setResp] = useState<AiUsageSummaryResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.aiUsageForUser(user.userId, range.value);
        if (!cancelled) {
          setResp(r);
          setPhase("ready");
        }
      } catch (e) {
        if (!cancelled) {
          setErr(friendlyError(e));
          setPhase("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user.userId]);

  return (
    <div
      class="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        class="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-background p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="mb-3 flex items-start justify-between gap-2">
          <div>
            <h2 class="text-lg font-bold">{user.userName}</h2>
            <p class="text-xs text-muted-foreground">{user.userEmail}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            class="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            <X className="size-4" />
          </button>
        </header>

        {phase === "loading" && <Skeleton class="h-64 w-full" />}
        {phase === "error" && (
          <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
            {err}
          </div>
        )}
        {phase === "ready" && resp && (
          <>
            <div class="mb-3 grid grid-cols-3 gap-2 text-xs">
              <Stat label="Eventos" value={String(resp.overall?.events ?? 0)} />
              <Stat
                label="Tokens"
                value={formatTokens(Number(resp.overall?.totalTokens ?? 0))}
              />
              <Stat
                label="Custo USD"
                value={formatUsdNano(Number(resp.overall?.costPlatformNano ?? 0))}
              />
            </div>
            <h3 class="mb-2 text-sm font-semibold">Últimos 100 eventos</h3>
            <div class="overflow-x-auto">
              <table class="w-full text-xs">
                <thead>
                  <tr class="border-b border-border text-left text-muted-foreground">
                    <th class="py-1">Quando</th>
                    <th class="py-1">Feature</th>
                    <th class="py-1">Modelo</th>
                    <th class="py-1">Modo</th>
                    <th class="py-1 text-right">Tokens</th>
                    <th class="py-1 text-right">Custo</th>
                    <th class="py-1">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {resp.recentEvents.map((e) => (
                    <tr key={e.id} class="border-b border-border/40">
                      <td class="py-1 whitespace-nowrap">
                        {new Date(e.createdAt).toLocaleString("pt-BR")}
                      </td>
                      <td class="py-1">{e.feature}</td>
                      <td class="py-1 font-mono">
                        {e.model}
                        {e.fallbackUsed ? (
                          <span
                            class="ml-1 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-700 dark:text-amber-300"
                            title="Caiu no provider de fallback (o primário falhou)"
                          >
                            fallback
                          </span>
                        ) : null}
                      </td>
                      <td class="py-1">
                        <span
                          class={`rounded-full px-1.5 py-0.5 text-[10px] ${
                            e.accessMode === "platform"
                              ? "bg-violet-500/15 text-violet-700 dark:text-violet-300"
                              : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                          }`}
                        >
                          {e.accessMode}
                        </span>
                      </td>
                      <td class="py-1 text-right">{e.totalTokens}</td>
                      <td class="py-1 text-right tabular-nums" title={e.costNanoUsd != null ? formatUsdNano(Number(e.costNanoUsd)) : ""}>
                        {formatUsdNanoRow(e.costNanoUsd ?? null)}
                      </td>
                      <td class="py-1">
                        {e.ok ? (
                          <span class="text-emerald-600 dark:text-emerald-400">✓</span>
                        ) : (
                          <span class="text-destructive" title={e.errorCode ?? ""}>
                            ✗ {e.errorCode}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div class="rounded-md border border-border bg-card px-3 py-2">
      <div class="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div class="text-base font-bold tabular-nums">{value}</div>
    </div>
  );
}

function CostTableModal({ onClose }: { onClose: () => void }) {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [rows, setRows] = useState<AiCostTableRow[]>([]);
  const [err, setErr] = useState<string | null>(null);

  async function loadCosts() {
    setPhase("loading");
    try {
      const r = await api.aiCostTable();
      setRows(r.rows);
      setPhase("ready");
    } catch (e) {
      setErr(friendlyError(e));
      setPhase("error");
    }
  }
  useEffect(() => {
    void loadCosts();
  }, []);

  async function editRow(row: AiCostTableRow) {
    const inputStr = await openPrompt({
      title: `Preço input (${row.provider}/${row.model})`,
      message:
        "Preço em décimos-de-centavo USD por 1k tokens de INPUT. Ex: 75 = 0.0075 cents = $0.000075/1k = $0.075/1M.",
      placeholder: String(row.inputPer1kTenthCents),
      confirmLabel: "Continuar"
    });
    if (inputStr == null) return;
    const outputStr = await openPrompt({
      title: `Preço output (${row.provider}/${row.model})`,
      message: "Preço em décimos-de-centavo USD por 1k tokens de OUTPUT.",
      placeholder: String(row.outputPer1kTenthCents),
      confirmLabel: "Salvar"
    });
    if (outputStr == null) return;
    const input = Number(inputStr);
    const output = Number(outputStr);
    if (!Number.isFinite(input) || input < 0 || !Number.isFinite(output) || output < 0) {
      void openAlert({ title: "Inválido", message: "Use números >= 0." });
      return;
    }
    try {
      await api.upsertAiCost({
        provider: row.provider,
        model: row.model,
        inputPer1kTenthCents: input,
        outputPer1kTenthCents: output
      });
      await loadCosts();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    }
  }

  return (
    <div
      class="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        class="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-background p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="mb-3 flex items-center justify-between">
          <h2 class="flex items-center gap-2 text-lg font-bold">
            <DollarSign className="size-5 text-primary" />
            Tabela de preços
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            class="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            <X className="size-4" />
          </button>
        </header>
        <p class="mb-3 text-xs text-muted-foreground">
          Preço por <strong>1k tokens</strong>, em <strong>décimos-de-centavo USD</strong>{" "}
          (ex: 75 = 0,0075¢ = $0,075/1M de tokens). Usado pra estimar custo
          dos events futuros — events passados ficam congelados no preço-da-época.
        </p>
        {phase === "loading" && <Skeleton class="h-64 w-full" />}
        {phase === "error" && (
          <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
            {err}
          </div>
        )}
        {phase === "ready" && (
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border text-left text-xs text-muted-foreground">
                <th class="py-2">Provider</th>
                <th class="py-2">Modelo</th>
                <th class="py-2 text-right">Input/1k</th>
                <th class="py-2 text-right">Output/1k</th>
                <th class="py-2">Atualizado</th>
                <th class="py-2 text-right">Ação</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} class="border-b border-border/40">
                  <td class="py-2 font-medium">{r.provider}</td>
                  <td class="py-2 font-mono text-xs">{r.model}</td>
                  <td class="py-2 text-right tabular-nums">{r.inputPer1kTenthCents}</td>
                  <td class="py-2 text-right tabular-nums">{r.outputPer1kTenthCents}</td>
                  <td class="py-2 text-xs">
                    {new Date(r.updatedAt).toLocaleDateString("pt-BR")}
                  </td>
                  <td class="py-2 text-right">
                    <button
                      type="button"
                      onClick={() => editRow(r)}
                      class="rounded-md border border-border bg-background px-2 py-0.5 text-xs hover:bg-accent"
                    >
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
